/**
 * MSML HEALTH Landing Page Content Configuration
 * Edit this file to update landing page copy without touching layout code
 */

export const landingContent = {
  meta: {
    title: "MSML HEALTH | Understand What AI Does to Humans",
    description: "Measure, explain, and improve AI-generated text for maximum human impact. Privacy-first evaluation instrument."
  },

  hero: {
    badge: "Human-Impact Intelligence",
    headline: "Decode how AI shapes your perception and experience.",
    subheadline: "Measure. Explain. Improve.",
    description: "MSML HEALTH is a precision instrument that evaluates AI-generated text for human impact then optimizes it while preserving intent.",
    primaryCTA: {
      text: "Try the Lens",
      href: "/signup"
    },
    secondaryCTA: {
      text: "See how it works",
      href: "#how-it-works"
    }
  },

  howItWorks: {
    label: "Process",
    title: "How It Works",
    subtitle: "A closed-loop system for AI output optimization",
    steps: [
      {
        number: "01",
        title: "Input AI Response",
        description: "Paste any AI-generated text—chatbot replies, generated content, automated communications.",
        icon: "input"
      },
      {
        number: "02",
        title: "Compute HQ Score",
        description: "Our deterministic engine analyzes the text across six human-impact dimensions.",
        icon: "calculate"
      },
      {
        number: "03",
        title: "Explain Drivers",
        description: "Understand exactly why the score is what it is—transparency in every dimension.",
        icon: "explain"
      },
      {
        number: "04",
        title: "Generate Improved Rewrite",
        description: "Receive an optimized version that maximizes human benefit while reducing harm.",
        icon: "optimize"
      },
      {
        number: "05",
        title: "Verify Intent Preservation",
        description: "An independent judge confirms the rewrite preserves original meaning and safety.",
        icon: "verify"
      }
    ]
  },

  dimensions: {
    label: "The Framework",
    title: "Six Dimensions of Human Impact",
    subtitle: "Every AI output is measured against what matters most to the humans who receive it.",
    items: [
      {
        name: "Agency",
        description: "Does the response empower the user to make their own decisions, or does it prescribe action without room for autonomy?",
        icon: "compass",
        color: "#2E79FF"
      },
      {
        name: "Clarity",
        description: "Is the message easy to understand? Does it reduce confusion or add to it?",
        icon: "eye",
        color: "#10B981"
      },
      {
        name: "Emotional Regulation",
        description: "Does the response help the user stay calm and grounded, or does it escalate anxiety or distress?",
        icon: "heart",
        color: "#EC4899"
      },
      {
        name: "Cognitive Load",
        description: "How much mental effort is required to process this? Less is often more.",
        icon: "brain",
        color: "#F5A623"
      },
      {
        name: "Safety & Risk",
        description: "Does the content introduce any potential for harm—physical, emotional, financial, or reputational?",
        icon: "shield",
        color: "#8B5CF6"
      },
      {
        name: "Dependency Risk",
        description: "Does the response foster healthy self-sufficiency, or create unhealthy reliance on AI assistance?",
        icon: "link",
        color: "#EF4444"
      }
    ]
  },

  beforeAfter: {
    label: "In Practice",
    title: "Before & After",
    subtitle: "See how MSML HEALTH transforms AI output for maximum human benefit.",
    example: {
      context: "User asks: \"I've been feeling really anxious about my job. What should I do?\"",
      before: {
        label: "Original AI Response",
        score: 42,
        text: "You should consider talking to your manager about your concerns. Also, make sure you're getting enough sleep and exercise. You might want to see a therapist if the anxiety persists. Here are 15 strategies for managing workplace anxiety: 1) Practice deep breathing 2) Set boundaries 3) Prioritize tasks 4) Take breaks 5) Limit caffeine 6) Exercise regularly 7) Sleep well 8) Talk to someone 9) Challenge negative thoughts 10) Practice mindfulness 11) Set realistic goals 12) Celebrate small wins 13) Create a supportive workspace 14) Learn to say no 15) Consider professional help.",
        issues: [
          "High cognitive load (15 items)",
          "Prescriptive without context",
          "May increase anxiety",
          "No agency preservation"
        ]
      },
      after: {
        label: "MSML-Optimized Response",
        score: 87,
        text: "That sounds really difficult—job anxiety can be exhausting.\n\nBefore jumping into solutions, it might help to sit with one question: What specifically is making you anxious? Is it the work itself, relationships with colleagues, job security, or something else?\n\nOnce you have a clearer picture, you'll be in a better position to decide what feels right for you. Some people find it helpful to talk it through with someone they trust. Others prefer to make small changes first.\n\nWhat resonates with you?",
        improvements: [
          "Acknowledges emotion first",
          "Reduces cognitive load",
          "Preserves user agency",
          "Invites reflection, not prescription"
        ]
      }
    }
  },

  whyNow: {
    label: "Context",
    title: "Why This Matters Now",
    subtitle: "AI is becoming the default interface between humans and information.",
    points: [
      {
        title: "2.5 billion",
        description: "People interact with AI chatbots weekly. Each response shapes how they think, feel, and act."
      },
      {
        title: "68%",
        description: "Of users report emotional responses to AI interactions—both positive and negative."
      },
      {
        title: "Zero",
        description: "Standard frameworks exist for measuring human impact of AI outputs at scale."
      }
    ],
    statement: "MSML HEALTH fills a critical gap: a deterministic, transparent, privacy-respecting way to measure and improve what AI does to the humans who use it."
  },

  privacy: {
    label: "Privacy First",
    title: "Your Data, Your Rules",
    subtitle: "Built for trust, not extraction.",
    features: [
      {
        title: "Ephemeral by Default",
        description: "Text is analyzed and discarded. Nothing persists unless you explicitly choose to save it.",
        icon: "ghost"
      },
      {
        title: "No Model Training",
        description: "Your data is never used to train global models. Ever. Your insights stay yours.",
        icon: "lock"
      },
      {
        title: "User-Controlled History",
        description: "Choose to save analyses for your own reference. Delete anytime. Export everything.",
        icon: "database"
      },
      {
        title: "Transparent Processing",
        description: "See exactly what happens to your text at every step. No black boxes.",
        icon: "eye"
      }
    ],
    cta: {
      text: "Read our privacy policy",
      href: "/privacy"
    }
  },

  useCases: {
    label: "Applications",
    title: "Who Uses MSML HEALTH",
    subtitle: "From individual creators to enterprise evaluation teams.",
    cases: [
      {
        title: "Content Creators",
        description: "Ensure AI-assisted content doesn't inadvertently harm your audience's wellbeing.",
        icon: "pen"
      },
      {
        title: "Healthcare AI Teams",
        description: "Evaluate patient-facing AI for emotional safety and appropriate agency preservation.",
        icon: "activity"
      },
      {
        title: "Customer Service",
        description: "Optimize automated responses for clarity and emotional regulation before deployment.",
        icon: "headphones"
      },
      {
        title: "EdTech Platforms",
        description: "Ensure AI tutors foster independence rather than dependency.",
        icon: "book"
      },
      {
        title: "Mental Health Apps",
        description: "Critical evaluation layer for any AI touching vulnerable populations.",
        icon: "heart"
      },
      {
        title: "Enterprise AI Governance",
        description: "Systematic evaluation of all AI outputs before they reach employees or customers.",
        icon: "building"
      }
    ]
  },

  pricing: {
    label: "Access",
    title: "Start Measuring Impact Today",
    subtitle: "Choose the plan that fits your needs.",
    plans: [
      {
        name: "Explorer",
        price: "Free",
        period: "",
        description: "For individuals getting started",
        features: [
          "10 analyses per month",
          "Basic HQ scoring",
          "Ephemeral processing only",
          "Community support"
        ],
        cta: {
          text: "Get Started",
          href: "/signup"
        },
        highlighted: false
      },
      {
        name: "Professional",
        price: "$29",
        period: "/month",
        description: "For creators and small teams",
        features: [
          "Unlimited analyses",
          "Full dimension breakdown",
          "Optimization & judge flows",
          "Saved history",
          "Priority support"
        ],
        cta: {
          text: "Start Free Trial",
          href: "/signup?plan=pro"
        },
        highlighted: true
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "For organizations at scale",
        features: [
          "Everything in Professional",
          "API access",
          "Custom scoring rubrics",
          "SSO / SAML integration",
          "Dedicated support",
          "On-premise option"
        ],
        cta: {
          text: "Contact Sales",
          href: "/contact"
        },
        highlighted: false
      }
    ]
  },

  footer: {
    cta: {
      headline: "Ready to understand what AI does to humans?",
      subheadline: "Start measuring human impact today.",
      primaryCTA: {
        text: "Try the Lens Free",
        href: "/signup"
      },
      secondaryCTA: {
        text: "Schedule a Demo",
        href: "/demo"
      }
    },
    links: {
      product: [
        { label: "Features", href: "#dimensions" },
        { label: "Pricing", href: "#pricing" },
        { label: "API Docs", href: "/docs" },
        { label: "Changelog", href: "/changelog" }
      ],
      company: [
        { label: "About", href: "/about" },
        { label: "Blog", href: "/blog" },
        { label: "Careers", href: "/careers" },
        { label: "Contact", href: "/contact" }
      ],
      legal: [
        { label: "Privacy", href: "/privacy" },
        { label: "Terms", href: "/terms" },
        { label: "Security", href: "/security" }
      ]
    },
    copyright: "© 2026 MSML HEALTH. All rights reserved."
  }
};

export default landingContent;
